/**
 * Rôles d'un compte et où mènent « publier un colis / un trajet » — règles de l'utilisateur du 2026-10-29 :
 * un expéditeur voit « Publier un colis », un transporteur « Publier un trajet », un compte qui a les deux rôles (et le
 * visiteur) « Publier une annonce » avec le choix entre les deux ; jamais un lien vers une page que le compte ne peut
 * pas ouvrir : sans le rôle, on mène à la demande de ce rôle, déjà choisi, avec la page visée en retour.
 * Testé dans utils/__tests__/roles.test.js ; affiché par components/PublishButton.jsx.
 */

export const SENDER = 'ROLE_SENDER';
export const CARRIER = 'ROLE_CARRIER';
export const ADMIN = 'ROLE_ADMIN';

export const hasRole = (user, role) => (user?.roles || []).includes(role);

/** 'visitor' | 'sender' | 'carrier' | 'both' | 'admin' */
export function accountKind(user) {
  if (!user) return 'visitor';
  const sender = hasRole(user, SENDER);
  const carrier = hasRole(user, CARRIER);
  if (sender && carrier) return 'both';
  if (sender) return 'sender';
  if (carrier) return 'carrier';
  return hasRole(user, ADMIN) ? 'admin' : 'visitor';
}

/** Ce qu'il faut pour publier chaque type d'annonce, et où se trouve son formulaire. */
export const PUBLISH = {
  colis: { role: SENDER, form: '/app/shipments/new', label: 'Publier un colis', signup: 'sender' },
  trajet: { role: CARRIER, form: '/app/trips/new', label: 'Publier un trajet', signup: 'carrier' },
};

/** Demande d'un rôle, déjà choisi, avec la page à rouvrir une fois le rôle accordé. */
export function roleRequestPath(role, next) {
  const params = new URLSearchParams({ role });
  if (next) params.set('next', next);
  return `/app/role-requests?${params.toString()}`;
}

/** Où mène « publier » ce type d'annonce : inscription (visiteur), formulaire (rôle présent), demande du rôle sinon. */
export function publishTarget(user, kind) {
  const p = PUBLISH[kind];
  if (!user) return `/register?role=${p.signup}`;
  return hasRole(user, p.role) ? p.form : roleRequestPath(p.role, p.form);
}

/** Les choix du bouton de publication : un seul pour un rôle, les deux pour un compte hybride ou un visiteur. */
export function publishOptions(user) {
  const kind = accountKind(user);
  const kinds = { visitor: ['colis', 'trajet'], both: ['colis', 'trajet'], sender: ['colis'], carrier: ['trajet'], admin: [] }[kind];
  return kinds.map((k) => ({ kind: k, label: PUBLISH[k].label, to: publishTarget(user, k), needsVerification: !!user }));
}

/**
 * Le lien vers l'autre côté de la plateforme, qui suit le rôle : « Vous voyagez ? » pour un visiteur ou un expéditeur
 * (inscription ou demande du rôle Transporteur), « Vous envoyez un colis ? » pour un transporteur ; rien pour un compte
 * qui a déjà les deux rôles ni pour l'admin.
 */
export function crossRoleLink(user) {
  const kind = accountKind(user);
  if (kind === 'visitor' || kind === 'sender') return { label: 'Vous voyagez ?', to: publishTarget(user, 'trajet') };
  if (kind === 'carrier') return { label: 'Vous envoyez un colis ?', to: publishTarget(user, 'colis') };
  return null;
}

export const ROLE_LABELS = { [SENDER]: 'Expéditeur', [CARRIER]: 'Transporteur', [ADMIN]: 'Administrateur' };

/**
 * Paramètres d'arrivée sur la page des demandes de rôle : rôle demandé (s'il est valide) et page de retour — une page de
 * l'application (`/app/…`) ou d'une annonce (`/annonces/…`), jamais une autre adresse ; même règle côté serveur
 * (`role_requests.safe_next_path`).
 */
export function readRoleRequestParams(search) {
  const q = new URLSearchParams(search || '');
  const role = [SENDER, CARRIER].includes(q.get('role')) ? q.get('role') : null;
  const next = /^\/(app|annonces)\/[A-Za-z0-9/_-]*$/.test(q.get('next') || '') && !(q.get('next') || '').includes('//') ? q.get('next') : null;
  return { role, next };
}
